import { useEffect, useState } from 'react';

import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { PreviewTable } from '../../../../components/PreviewTable';
import { todayCalendarDate } from '../../../../components/calendar/monthGrid';
import { DateField } from '../../../../components/inputs/DateField';
import { RadioGroup } from '../../../../components/inputs/RadioGroup';
import { Select } from '../../../../components/inputs/Select';
import { TextField } from '../../../../components/inputs/TextField';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import type { SurveyQuestion } from '../../survey/surveyTypes';
import { personnelApi } from '../personnelApi';
import type { PersonnelCatalogEntry, PersonnelSurveyAnswer } from '../personnelTypes';
import { sampleSurveyAnswer } from '../utils/sampleSurveyAnswer';

/** `''` is unanswered, and it is what the form opens on — never a defaulted "No". */
type AnswerChoice = '' | 'yes' | 'no';

/**
 * Answers ONE survey question — the modal the panel's Update button opens.
 *
 * **Dedicated to the question it was opened from.** There is no picker: the reader chose the
 * question by choosing whose row to press, so the form has nothing left to ask about it. The
 * question is stated at the top as a read-only fact, which is the Table Display View's job —
 * the reader is reading that, and answering everything below it.
 *
 * **The question decides what the form asks.** Whether there is a details box, a date, a case
 * status, an ID number or a country picker is the questionnaire's answer — the `collects*` flags
 * on the row. A control that can never apply is noise; one the form quietly ignores is worse.
 *
 * **And the answer decides it again.** The follow-ups appear on a Yes and vanish on a No,
 * because the form's own words are "If YES, give details". The server clears them either way, so
 * a reader who types the details and then corrects the answer to No cannot leave a paragraph
 * behind on a record that now says there is nothing to say.
 */
export function EditSurveyAnswerModal({
  personnelId,
  question,
  editing,
  onClose,
  onSaved,
}: {
  personnelId: string;
  /** The question being answered. Fixed by the row this was opened from. */
  question: SurveyQuestion;
  /** The answer already on file, or null when this question is unanswered. */
  editing: PersonnelSurveyAnswer | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [countries, setCountries] = useState<PersonnelCatalogEntry[]>([]);
  const [loadingCountries, setLoadingCountries] = useState(question.collectsCountry);

  const [answer, setAnswer] = useState<AnswerChoice>(
    editing ? (editing.answer ? 'yes' : 'no') : '',
  );
  const [details, setDetails] = useState(editing?.details ?? '');
  const [dateFiled, setDateFiled] = useState(editing?.dateFiled ?? '');
  const [caseStatus, setCaseStatus] = useState(editing?.caseStatus ?? '');
  const [idNumber, setIdNumber] = useState(editing?.idNumber ?? '');
  const [countryId, setCountryId] = useState<string | null>(editing?.countryId ?? null);

  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    /*
      Fetched only for a question that asks which country — item 39 and nothing else. Every
      other question would be paying for a catalog it can never offer, on a modal that opens
      twelve times per record.
    */
    if (!question.collectsCountry) return;

    const controller = new AbortController();

    personnelApi
      .catalogs(controller.signal)
      .then((catalogs) => {
        if (!controller.signal.aborted) setCountries(catalogs.countries);
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        setError(
          caught instanceof ApiError ? caught : new ApiError('Could not load the countries.', 0),
        );
        // No focus call: a failed picker load names no field.
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoadingCountries(false);
      });

    return () => controller.abort();
  }, [question.collectsCountry]);

  const isYes = answer === 'yes';

  const detailsMissing = isYes && question.collectsDetails && details.trim() === '';
  const countryMissing = isYes && question.collectsCountry && countryId === null;

  const valid = answer !== '' && !detailsMissing && !countryMissing;
  const canSave = valid && confirmed && !saving;

  /*
    Fills the form from the question this modal was opened for, so the follow-ups follow whatever
    that row asks for. It answers YES, which is the branch that draws every one of them — a
    generated "No" would fill one radio and leave the rest of the form untouched.
  */
  function generate() {
    const sample = sampleSurveyAnswer(question, countries);

    setAnswer(sample.answer);
    setDetails(sample.details);
    setDateFiled(sample.dateFiled);
    setCaseStatus(sample.caseStatus);
    setIdNumber(sample.idNumber);
    setCountryId(sample.countryId);
  }

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    /*
      The follow-ups are sent only where the question ASKS for them and the answer is Yes. The
      server refuses a field the question does not ask for — that is data nobody can interpret —
      and clears the rest on a No, so sending them would be sending values about to be dropped.
    */
    const request = {
      surveyQuestionId: question.id,
      answer: isYes,
      details: isYes && question.collectsDetails ? details.trim() || null : null,
      dateFiled: isYes && question.collectsDate ? dateFiled || null : null,
      caseStatus: isYes && question.collectsCaseStatus ? caseStatus.trim() || null : null,
      idNumber: isYes && question.collectsIdNumber ? idNumber.trim() || null : null,
      countryId: isYes && question.collectsCountry ? countryId : null,
    };

    try {
      if (editing) await personnelApi.updateSurveyAnswer(personnelId, editing.id, request);
      else await personnelApi.addSurveyAnswer(personnelId, request);

      onSaved();
    } catch (caught) {
      const failure =
        caught instanceof ApiError ? caught : new ApiError('Could not save this answer.', 0);

      setError(failure);
      focusValidationError(failure, {
        idPrefix: 'survey-answer',
        fieldIds: {
          // A `<RadioGroup>` puts its id on each OPTION, so nothing sits at the derived id.
          answer: 'survey-answer-answer-yes',

          // The server names the catalog field; the control that chooses it is the picker.
          countryId: 'survey-answer-country',

          /*
            The question is fixed by the row, so this form has no control for it — the only way
            the server names it is a question retired in another tab while this modal was open.
            Focus goes to the head of the form, beside the banner carrying the sentence.
          */
          surveyQuestionId: 'survey-answer-answer-yes',
        },
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      // Names the item, so a reader who pressed the wrong row sees it before they answer.
      title={`Item ${question.number}`}
      size="2xl"
      id="survey-answer-modal"
      /*
        Withheld while the country catalog is still in flight, and while it is EMPTY on a
        question that asks for one: the generator would leave a required foreign key null and
        the form still refusing to save, which reads as the button being broken rather than as
        a catalog nobody has filled.
      */
      autogenerate={{
        onGenerate: generate,
        disabled:
          saving || loadingCountries || (question.collectsCountry && countries.length === 0),
      }}
      confirm={{
        id: 'survey-answer-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button id="survey-answer-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id="survey-answer-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        {/*
          The question is a FACT being shown, not a value being asked for, so it takes the Table
          Display View rather than a field's label. That is the distinction this form is made of:
          everything above the rule is read, everything below it is answered.
        */}
        <PreviewTable
          variant="table-display"
          rows={[{ label: question.number, value: question.text }]}
        />

        {/*
          The question's own guidance, rendered once and under the question it belongs to — item
          34a carries the meaning of consanguinity, item 40b says a card still being applied for
          is a valid answer.
        */}
        {question.guidance ? (
          <p className="text-xs text-slate-500">{question.guidance}</p>
        ) : null}

        <RadioGroup<AnswerChoice>
          id="survey-answer-answer"
          label="Answer"
          required
          value={answer}
          onChange={setAnswer}
          options={[
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' },
          ]}
          error={answer === '' ? 'Answer Yes or No.' : error?.fieldError('answer')}
          disabled={saving}
        />

        {/*
          Drawn on a Yes only, and only for what the question asks. Nothing is greyed out
          instead: a reader who answered No is not being asked to account for something they
          have just said did not happen.
        */}
        {isYes && question.collectsDetails ? (
          <TextField
            id="survey-answer-details"
            label="Details"
            required
            description="What the form asks for after a Yes — who, what, and where it applies."
            value={details}
            onChange={(e) => setDetails(e.target.value)}
            error={
              detailsMissing ? 'Give the details this answer calls for.' : error?.fieldError('details')
            }
            disabled={saving}
          />
        ) : null}

        {isYes && (question.collectsDate || question.collectsCaseStatus) ? (
          <div className="grid gap-4 sm:grid-cols-2">
            {question.collectsDate ? (
              <DateField
                id="survey-answer-date-filed"
                label="Date filed"
                // Capped at today: a case cannot have been filed in the future.
                max={todayCalendarDate()}
                description="Leave blank where the employee cannot recall it."
                value={dateFiled}
                onChange={setDateFiled}
                error={error?.fieldError('dateFiled')}
                disabled={saving}
              />
            ) : null}

            {question.collectsCaseStatus ? (
              <TextField
                id="survey-answer-case-status"
                label="Status of case"
                description="Where the matter stands now, as the employee states it."
                value={caseStatus}
                onChange={(e) => setCaseStatus(e.target.value)}
                error={error?.fieldError('caseStatus')}
                disabled={saving}
              />
            ) : null}
          </div>
        ) : null}

        {isYes && question.collectsIdNumber ? (
          <TextField
            id="survey-answer-id-number"
            label="ID number"
            description="The number on the card. Leave blank where one has been applied for and not yet issued."
            value={idNumber}
            onChange={(e) => setIdNumber(e.target.value)}
            error={error?.fieldError('idNumber')}
            disabled={saving}
          />
        ) : null}

        {isYes && question.collectsCountry ? (
          <Select
            id="survey-answer-country"
            label="Country"
            required
            loading={loadingCountries}
            // A COUNTRY, not a nationality: "American" is not an answer to "which country".
            description="The country of which the employee is an immigrant or permanent resident."
            value={countryId}
            onChange={(next) => setCountryId(next || null)}
            options={countries.map((c) => ({ value: c.id, label: c.name }))}
            error={countryMissing ? 'Choose the country.' : error?.fieldError('countryId')}
            disabled={saving}
          />
        ) : null}
      </div>
    </Modal>
  );
}
